import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import type { NotificationHistoryItem } from "./types";
import {
  mapMemberNotification,
  type MemberNotificationRow,
} from "./memberNotificationModel";

export type { MemberNotificationRow } from "./memberNotificationModel";

/**
 * The bell's list. Rows live on the server (member_notifications, written by
 * database triggers) so every device shows the same notifications and read
 * state. This module keeps the loaded rows in memory for the bell and
 * writes read/delete back. "nomi:notifications-updated" tells the UI.
 */

const MAX_ITEMS = 100;
let items: NotificationHistoryItem[] = [];

const emit = () =>
  window.dispatchEvent(new CustomEvent("nomi:notifications-updated"));

const sortNewest = (list: NotificationHistoryItem[]) =>
  [...list]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .slice(0, MAX_ITEMS);

/** Replace the list (initial load / refetch). */
export const setNotificationHistory = (rows: MemberNotificationRow[]) => {
  items = sortNewest(rows.map(mapMemberNotification));
  emit();
};

/** Insert or update one row (Realtime). */
export const upsertNotificationHistory = (row: MemberNotificationRow) => {
  const entry = mapMemberNotification(row);
  items = sortNewest([entry, ...items.filter((item) => item.id !== entry.id)]);
  emit();
  return entry;
};

export const removeNotificationHistoryItem = (id: string) => {
  items = items.filter((item) => item.id !== id);
  emit();
};

export const getNotificationHistory = () => items;

export const countUnreadNotificationHistory = () =>
  items.filter((item) => !item.read).length;

const markReadOnServer = async (ids: string[]) => {
  const numeric = ids.map(Number).filter(Number.isFinite);
  if (!numeric.length) return;
  await supabase
    .from("member_notifications")
    .update({ read_at: new Date().toISOString() })
    .in("id", numeric)
    .is("read_at", null);
};

export const markNotificationHistoryItemsRead = (ids: string[]) => {
  const idSet = new Set(ids);
  if (!idSet.size) return;
  let changed = false;
  items = items.map((item) => {
    if (!idSet.has(item.id) || item.read) return item;
    changed = true;
    return { ...item, read: true };
  });
  if (!changed) return;
  emit();
  void markReadOnServer([...idSet]);
};

export const markNotificationHistoryRead = (id: string) =>
  markNotificationHistoryItemsRead([id]);

export const markAllNotificationHistoryRead = () =>
  markNotificationHistoryItemsRead(
    items.filter((item) => !item.read).map((item) => item.id),
  );

const TICKET_NOTIFICATION_CATEGORIES = new Set([
  "tickets_message",
  "tickets_assigned",
]);

export const markTicketNotificationsRead = () =>
  markNotificationHistoryItemsRead(
    items
      .filter(
        (item) =>
          !item.read && TICKET_NOTIFICATION_CATEGORIES.has(item.category),
      )
      .map((item) => item.id),
  );

/** "Clear all": deletes the member's notifications on the server. */
export const clearNotificationHistory = () => {
  const ids = items.map((item) => Number(item.id)).filter(Number.isFinite);
  items = [];
  emit();
  if (ids.length) {
    void supabase.from("member_notifications").delete().in("id", ids);
  }
};
